/**
 * Pick a template and a kind of person; see the cards they would get, when
 * each is due, and what each one waits on. Nothing is saved.
 *
 * `PreviewRun` is the part that runs one template, so the editor shows the
 * same thing for the draft it has open (PRD 10.4, "simulate the workflow as a
 * specific employee type"); `TemplatePreview` is the page that picks a
 * starter for somebody with no organisation yet.
 */

import { useMemo, useState, type ReactNode } from "react";
import {
  EMPLOYEE_TYPES,
  EMPLOYMENT_STATUSES,
  OFFBOARDING_TRIGGERS,
  STARTER_TEMPLATES,
  TEMPLATE_LIBRARY,
  libraryTemplate,
  type Answer,
  type Template,
} from "@utuwrk/core";
import { preview, triggerFor, type PreviewRow } from "../lib/preview.js";
import { anchorLabel } from "../lib/words.js";
import { PageHeader } from "./bits.js";
import { Questions } from "./Questions.js";

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

/** Two weeks from today, which puts most of a template's pre-arrival work in the future. */
function inTwoWeeks(): string {
  const date = new Date(Date.now() + 14 * 86_400_000);
  return date.toISOString().slice(0, 10);
}

const STATE_LABEL: Record<PreviewRow["state"], string> = {
  ready: "Ready",
  waiting: "Waiting",
  sent: "Sent",
};

/** The icon inside each state pill, so the state is never carried by colour alone (PRD 10.7). */
function StateIcon({ state }: { state: PreviewRow["state"] }) {
  const path = {
    ready: "M4.5 3l3.5 3-3.5 3",
    waiting: "M6 3.2V6l1.8 1.2",
    sent: "M2 3.5h8v5H2zM2 3.5l4 3 4-3",
  }[state];
  return (
    <svg viewBox="0 0 12 12" aria-hidden="true" className="pill-icon">
      {state === "waiting" ? <circle cx="6" cy="6" r="4.5" /> : null}
      <path d={path} />
    </svg>
  );
}

function words(value: string): string {
  return value.replace(/-/g, " ").replace(/^./, (first) => first.toUpperCase());
}

export function TemplatePreview() {
  const [templateId, setTemplateId] = useState(STARTER_TEMPLATES[0]!.id);
  const template = libraryTemplate(templateId) ?? STARTER_TEMPLATES[0]!;
  return (
    <section className="preview">
      <PageHeader
        title="Preview a template"
        meta="The real engine, run in your browser with nothing saved: the cards this person would get, when each is due, and what each waits on."
      />
      <PreviewRun
        template={template}
        picker={
          <label className="field">
            <span>Template</span>
            <select value={templateId} onChange={(event) => setTemplateId(event.target.value)}>
              {TEMPLATE_LIBRARY.map(({ template: starter }) => (
                <option key={starter.id} value={starter.id}>
                  {starter.name}
                </option>
              ))}
            </select>
          </label>
        }
      />
    </section>
  );
}

/** One template, run for the kind of person chosen above the cards. */
export function PreviewRun({ template, picker }: { template: Template; picker?: ReactNode }) {
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [employeeType, setEmployeeType] = useState<string>("office");
  const [employmentStatus, setEmploymentStatus] = useState<string>("full-time");
  const [trigger, setTrigger] = useState<string>("resignation");
  const [anchorDate, setAnchorDate] = useState(inTwoWeeks);
  const [hipaa, setHipaa] = useState(false);

  const result = useMemo(
    () =>
      preview({
        templateId: template.id,
        template,
        answers,
        employeeType,
        employmentStatus,
        trigger,
        anchorDate,
        hipaa,
        timeZone,
        now: Date.now(),
      }),
    [template, answers, employeeType, employmentStatus, trigger, anchorDate, hipaa],
  );
  const { asks } = result;

  return (
    <>
      <div className="panel">
        <div className="controls">
          {picker}

          {asks.employeeType ? (
            <label className="field">
              <span>Employee type</span>
              <select value={employeeType} onChange={(event) => setEmployeeType(event.target.value)}>
                {EMPLOYEE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {words(type)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {asks.employmentStatus ? (
            <label className="field">
              <span>Status</span>
              <select value={employmentStatus} onChange={(event) => setEmploymentStatus(event.target.value)}>
                {EMPLOYMENT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {words(status)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {asks.trigger ? (
            <label className="field">
              <span>Why they are leaving</span>
              <select value={trigger} onChange={(event) => setTrigger(event.target.value)}>
                {OFFBOARDING_TRIGGERS.map((reason) => (
                  <option key={reason} value={reason}>
                    {words(reason)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {asks.anchorDate ? (
            <label className="field">
              <span>
                {anchorLabel({ kind: result.template.kind, trigger: triggerFor(result.template, trigger) })}
              </span>
              <input
                type="date"
                value={anchorDate}
                onChange={(event) => event.target.value && setAnchorDate(event.target.value)}
              />
            </label>
          ) : null}

          <label className="check">
            <input type="checkbox" checked={hipaa} onChange={(event) => setHipaa(event.target.checked)} />
            <span>HIPAA site</span>
          </label>
        </div>
        <Questions
          questions={asks.questions}
          answers={Object.fromEntries(
            asks.questions.map((question) => [question.key, answers[question.key] ?? "yes"]),
          )}
          onChange={(key, answer) => setAnswers((was) => ({ ...was, [key]: answer }))}
        />
      </div>

      {result.notes.length > 0 ? (
        <ul className="notes" aria-label="Things to know">
          {result.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}

      <ol className="cards">
        {result.rows.map((row) => (
          <li key={row.key} className={`card${row.state === "waiting" ? " card-later" : ""}`}>
            <div className="card-top">
              <h2 className="card-title">
                {row.kind === "notify" ? <span className="card-kind">Notify · </span> : null}
                {row.title}
              </h2>
              <span className={`pill s-${row.state}`}>
                <StateIcon state={row.state} />
                {STATE_LABEL[row.state]}
              </span>
            </div>
            <p className="card-meta">
              <span className="chip">{row.department}</span>
              <span>{row.when}</span>
              {row.dueDay ? <span>due {row.dueDay}</span> : null}
              {row.timeCritical ? <span className="pill s-danger">Time-critical</span> : null}
              {row.compliance ? <span className="chip">Compliance</span> : null}
              {row.compressed ? <span className="warn">Moved up: that day had already passed</span> : null}
            </p>
            {row.waitsOn.length > 0 ? <p className="card-meta">Waits on {row.waitsOn.join(", ")}</p> : null}
            {row.softWaitsOn.length > 0 ? (
              <p className="card-meta">Best after {row.softWaitsOn.join(", ")}</p>
            ) : null}
            {row.signoffs.length > 0 ? (
              <p className="card-meta">Sign-off from {row.signoffs.join(", ")}</p>
            ) : null}
            {row.reads.length > 0 ? <p className="card-meta">Reads {row.reads.join("; ")}</p> : null}
            {row.branches.length > 0 ? <p className="card-meta">{row.branches.join("; ")}</p> : null}
            {row.opensOn ? <p className="card-meta">Opens on {row.opensOn}</p> : null}
            {row.gate ? (
              <p className="card-meta">Gate: what comes after waits for it, and it cannot be skipped</p>
            ) : null}
            {row.mailTo ? <p className="card-meta">Also mails {row.mailTo}</p> : null}
          </li>
        ))}
      </ol>

      {result.excluded.length > 0 ? (
        <details className="panel excluded">
          <summary>
            {result.excluded.length} card{result.excluded.length === 1 ? "" : "s"} this person does not get
          </summary>
          <ul>
            {result.excluded.map((title) => (
              <li key={title}>{title}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </>
  );
}
